import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Wand2, ChevronRight, X, AlertTriangle, Lightbulb } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InfluenceTab() {
  const [content, setContent] = useState(null);
  const [active, setActive] = useState(null);
  const [scenario, setScenario] = useState(null);

  useEffect(() => {
    axios.get(`${API}/influence/content`, { withCredentials: true })
      .then(({ data }) => setContent(data))
      .catch(() => {});
  }, []);

  if (!content) return <div className="text-center py-8 text-sm opacity-60">Laddar...</div>;

  const tech = active ? content.techniques.find((t) => t.id === active) : null;
  const sc = scenario ? content.scenarios.find((s) => s.id === scenario) : null;

  return (
    <div className="space-y-4 pb-6" data-testid="influence-tab">
      <div className="neo-card p-5 bg-gradient-to-br from-[#FFE8F5] via-white to-[#C8B6FF]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#FFC8DD] border-2 border-black flex items-center justify-center">
            <Wand2 className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-['Fredoka']">Påverka 🪄</h2>
            <p className="text-xs text-gray-700">Subtila tekniker — så frön, låt andra plocka frukten</p>
          </div>
        </div>
      </div>

      {/* Principle */}
      <div className="neo-card p-4 bg-[#FDF0D5]" data-testid="influence-principle">
        <h3 className="font-black text-sm">{content.principle.title}</h3>
        <p className="text-xs italic opacity-80 mb-2">{content.principle.subtitle}</p>
        <ul className="space-y-1">
          {content.principle.rules.map((r, i) => (
            <li key={i} className="flex gap-2 text-xs">
              <span className="text-base">{r.emoji}</span>
              <span>{r.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Scenarios */}
      <div className="neo-card p-4 bg-white" data-testid="influence-scenarios">
        <h3 className="font-bold flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4" /> Färdiga scenarier</h3>
        <p className="text-[11px] opacity-70 italic mb-2">Klicka för en steg-för-steg-spelplan:</p>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {content.scenarios.map((s) => (
            <button
              key={s.id}
              onClick={() => setScenario(s.id)}
              className="neo-card p-2.5 bg-[#E8F5FF] hover:bg-[#C8E0F5] text-left flex items-start gap-2 transition"
              data-testid={`influence-scenario-${s.id}`}
            >
              <div className="flex-1">
                <h4 className="font-bold text-sm">{s.title}</h4>
                <p className="text-[10px] opacity-60 mt-0.5">{s.uses.length} tekniker · {s.playbook.length} steg</p>
              </div>
              <ChevronRight className="w-4 h-4 mt-1 opacity-50" />
            </button>
          ))}
        </div>
      </div>

      {/* Techniques */}
      <div className="neo-card p-4 bg-white" data-testid="influence-techniques">
        <h3 className="font-bold mb-2">10 tekniker</h3>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {content.techniques.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className="neo-card p-2.5 bg-[#FFF5E6] hover:bg-[#FFE8A3] text-left flex items-start gap-2 transition"
              data-testid={`influence-tech-${t.id}`}
            >
              <span className="text-2xl shrink-0">{t.emoji}</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm">{t.title}</h4>
                <p className="text-[11px] opacity-80 leading-snug">{t.core}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 opacity-50 mt-1 shrink-0" />
            </button>
          ))}
        </div>
      </div>

      {/* Technique modal */}
      {tech && (
        <div className="fixed inset-0 z-[210] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setActive(null)} data-testid="influence-modal">
          <div className="neo-card bg-white p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold flex items-center gap-2">
                <span className="text-3xl">{tech.emoji}</span> {tech.title}
              </h3>
              <button onClick={() => setActive(null)} className="neo-btn p-1.5 bg-white" data-testid="influence-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm font-bold border-l-[3px] border-black pl-2 italic mb-3">"{tech.core}"</p>
            <div className="mb-3">
              <h4 className="text-[10px] font-bold uppercase opacity-60 tracking-wider mb-1">Så gör du</h4>
              <p className="text-sm leading-relaxed">{tech.how}</p>
            </div>
            <div className="border-2 border-black rounded-xl p-2.5 bg-[#FDF0D5] mb-3">
              <h4 className="text-[10px] font-bold uppercase opacity-60 tracking-wider mb-1">Exempel</h4>
              <p className="text-sm italic">{tech.example}</p>
            </div>
            {tech.warning && (
              <div className="border-2 border-black rounded-xl p-2.5 bg-[#FFE8E8] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-xs">{tech.warning}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scenario modal */}
      {sc && (
        <div className="fixed inset-0 z-[210] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setScenario(null)} data-testid="scenario-modal">
          <div className="neo-card bg-white p-4 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold">🎬 {sc.title}</h3>
              <button onClick={() => setScenario(null)} className="neo-btn p-1.5 bg-white" data-testid="scenario-modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] opacity-70 mb-3">
              Tekniker: {sc.uses.map((id) => {
                const t = content.techniques.find((x) => x.id === id);
                return t ? `${t.emoji} ${t.title}` : id;
              }).join(' · ')}
            </p>
            <ol className="space-y-2">
              {sc.playbook.map((step, i) => (
                <li key={i} className="flex gap-2 items-start" data-testid={`scenario-step-${i}`}>
                  <div className="w-7 h-7 rounded-full border-2 border-black bg-[#FFE8A3] flex items-center justify-center text-xs font-black shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-sm leading-relaxed pt-0.5">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
